<template>
  <div class="support-bell-wrapper">
    <b-dropdown
      ref="dropdown"
      right
      no-caret
      toggle-class="support-bell-toggle"
      menu-class="support-bell-menu"
      @show="handleDropdownShow"
    >
      <template #button-content>
        <button type="button" class="support-bell-button">
          <i class="fa fa-bell" aria-hidden="true"></i>
          <span v-if="unreadCount > 0" class="support-bell-badge">
            {{ unreadCount > 99 ? "99+" : unreadCount }}
          </span>
        </button>
      </template>

      <div class="support-bell-header">
        <div>
          <strong>Suporte</strong>
          <small class="d-block text-muted"
            >Novos chamados e movimentações</small
          >
        </div>
        <b-button
          variant="link"
          size="sm"
          class="support-bell-refresh"
          :disabled="loading"
          @click.stop="loadNotifications"
        >
          Atualizar
        </b-button>
      </div>

      <b-dropdown-divider />

      <div v-if="loading" class="support-bell-state">
        <b-spinner small></b-spinner>
        <span class="ml-2">Carregando...</span>
      </div>

      <div
        v-else-if="groupedNotifications.length === 0"
        class="support-bell-state"
      >
        <i class="fa fa-check-circle text-success mr-2" aria-hidden="true"></i>
        <span>Nenhuma notificação pendente</span>
      </div>

      <div v-else class="support-bell-list">
        <button
          v-for="notification in groupedNotifications"
          :key="notification._id"
          type="button"
          class="support-bell-item"
          @click="openNotification(notification)"
        >
          <div class="support-bell-item-top">
            <strong>{{ notification.supportTicket.protocol }}</strong>
            <b-badge variant="secondary" pill>
              {{ notification.count }}
            </b-badge>
          </div>
          <div class="support-bell-item-title">
            {{ eventLabel(notification.type) }}
          </div>
          <div class="support-bell-item-meta">
            <span>{{ notification.supportTicket.module || "Suporte" }}</span>
            <span>{{ formatDate(notification.createdAt) }}</span>
          </div>
        </button>
      </div>

      <b-dropdown-divider />

      <b-dropdown-item @click="openAllNotifications">
        Ver todas
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
import NotificationType from "../../../../backend/models/NotificationType.ts";
import { markAsRead } from "../../api/notifications";
import { getSupportNotifications } from "../../api/support";

const SUPPORT_NOTIFICATION_REFRESH_INTERVAL = 30000;

export default {
  name: "SupportNotificationsBell",
  data() {
    return {
      loading: false,
      notifications: [],
      refreshInterval: null,
      NotificationType,
    };
  },
  computed: {
    unreadCount() {
      return this.notifications.length;
    },
    groupedNotifications() {
      const grouped = {};

      for (const notification of this.notifications) {
        if (!notification.supportTicket || !notification.supportTicket._id) {
          continue;
        }

        const key = `${notification.supportTicket._id}|${notification.type}`;
        grouped[key] = grouped[key] || [];
        grouped[key].push(notification);
      }

      return Object.keys(grouped)
        .map((key) => {
          const items = grouped[key].sort((a, b) => b._id - a._id);
          const first = items[0];

          return {
            _id: first._id,
            type: first.type,
            createdAt: first.createdAt,
            supportTicket: first.supportTicket,
            count: items.length,
          };
        })
        .sort((a, b) => b._id - a._id)
        .slice(0, 8);
    },
  },
  created() {
    this.loadNotifications();
    this.setupRefresh();
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    setupRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
      }

      this.refreshInterval = setInterval(() => {
        this.loadNotifications();
      }, SUPPORT_NOTIFICATION_REFRESH_INTERVAL);
    },
    async handleDropdownShow() {
      await this.loadNotifications();
    },
    async loadNotifications() {
      this.loading = true;

      try {
        const response = await getSupportNotifications();
        this.notifications = Array.isArray(response && response.data)
          ? response.data
          : [];
      } catch (error) {
        this.notifications = [];
      } finally {
        this.loading = false;
      }
    },
    eventLabel(type) {
      const labels = {
        [NotificationType.NEW_SUPPORT_TICKET]: "Novo chamado",
        [NotificationType.NEW_SUPPORT_MESSAGE]: "Nova movimentação",
        [NotificationType.NEW_SUPPORT_STATUS]: "Atualização de status",
      };

      return labels[type] || "Nova movimentação";
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      return new Date(value).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    async openNotification(notification) {
      await markAsRead(notification);

      if (this.$refs.dropdown) {
        this.$refs.dropdown.hide(true);
      }

      if (this.$store.getters.profile.role === "root") {
        this.$router.push("/suporte");
        return;
      }

      this.$router.push(`/suporte/${notification.supportTicket._id}`);
    },
    openAllNotifications() {
      if (this.$refs.dropdown) {
        this.$refs.dropdown.hide(true);
      }

      this.$router.push("/notificacoes/suporte");
    },
  },
};
</script>

<style scoped>
.support-bell-wrapper {
  display: flex;
  justify-content: flex-end;
}

.support-bell-wrapper ::v-deep .support-bell-toggle {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.support-bell-wrapper ::v-deep .support-bell-toggle:hover,
.support-bell-wrapper ::v-deep .support-bell-toggle:focus,
.support-bell-wrapper ::v-deep .support-bell-toggle:active,
.support-bell-wrapper ::v-deep .show > .support-bell-toggle.dropdown-toggle {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.support-bell-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #fff;
  color: #425466;
  box-shadow: none;
  padding: 0;
}

.support-bell-button:hover {
  background: #fff;
  color: #1f2937;
}

.support-bell-button i {
  font-size: 1.32rem;
}

.support-bell-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: #d93025;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 22px;
}

.support-bell-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-width: 320px;
  padding: 0.9rem 1rem 0.5rem;
}

.support-bell-refresh {
  padding-top: 0;
}

.support-bell-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 320px;
  padding: 1rem;
  color: #6b7280;
}

.support-bell-list {
  max-height: 360px;
  overflow-y: auto;
}

.support-bell-item {
  display: block;
  width: 100%;
  border: 0;
  border-bottom: 1px solid #edf2f7;
  background: #fff;
  padding: 0.85rem 1rem;
  text-align: left;
}

.support-bell-item:hover {
  background: #f8fafc;
}

.support-bell-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.support-bell-item-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.2rem;
}

.support-bell-item-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  color: #6b7280;
  font-size: 0.78rem;
}
</style>
